// @ts-expect-error module not typed
import EmailAddressRegex from 'PatternValidationJS/regex/EmailAddressRegex';
// @ts-expect-error module not typed
import UrlRegexFactory from 'PatternValidationJS/regex/factories/UrlRegexFactory';
import { Fragment, Node } from 'hubspot-prosemirror/pm/model';
import { ensureUrlHasProtocol } from '../../../draft-transplant/utils/libUtils';

export type LinkifyOptions = {
  autoDetectBareUrls?: boolean;
};

const emailSource = EmailAddressRegex.source.replace(/^\^(.*)\$$/, '$1');

const mailtoRegex = `mailto:${emailSource}`;

const urlSourceWithProtocol = UrlRegexFactory({
  protocolRequired: true,
}).source.replace(/^\^(.*)\$$/, '$1');

// Known limitation: matches any token containing a `.`, so file names
// (`readme.txt`) and typos (`about.And`) will also be linkified. A canonical
// fix would require a TLD allowlist; we accept this limitation, matching
// DraftJS behavior.
const urlSourceBareAllowed = UrlRegexFactory({
  protocolRequired: false,
}).source.replace(/^\^(.*)\$$/, '$1');

const emailFullMatchRegex = new RegExp(`^(?:${emailSource})$`);

function getUrlSource(autoDetectBareUrls: boolean): string {
  return autoDetectBareUrls ? urlSourceBareAllowed : urlSourceWithProtocol;
}

function buildUrlRegex(autoDetectBareUrls: boolean): RegExp {
  return new RegExp(
    `\\b(${getUrlSource(autoDetectBareUrls)})|(${mailtoRegex})\\b`,
    'g'
  );
}

const VALID_DOMAIN_LABEL = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const HAS_PROTOCOL = /^(?:https?:)?\/\//;
const TRAILING_PUNCTUATION = /[?.,]+$/;

function hasValidDomainLabels(urlMatch: string): boolean {
  const withoutProtocol = urlMatch.replace(HAS_PROTOCOL, '');
  const domainWithPort = withoutProtocol.split(/[/?#]/)[0];
  const domain = domainWithPort.replace(/:\d+$/, '');
  const labels = domain.split('.');
  return labels.every((label) => VALID_DOMAIN_LABEL.test(label));
}

function isBareUrlWithInvalidDomain(text: string): boolean {
  const cleaned = text.replace(TRAILING_PUNCTUATION, '');
  return !HAS_PROTOCOL.test(cleaned) && !hasValidDomainLabels(cleaned);
}

function getEmailRanges(text: string): Array<[number, number]> {
  const emailRegex = new RegExp(emailSource, 'g');
  const ranges: Array<[number, number]> = [];
  let match = emailRegex.exec(text);
  while (match) {
    ranges.push([match.index, match.index + match[0].length]);
    match = emailRegex.exec(text);
  }
  return ranges;
}

function isInsideEmail(
  start: number,
  end: number,
  emailRanges: Array<[number, number]>
): boolean {
  return emailRanges.some(
    ([emailStart, emailEnd]) => start < emailEnd && end > emailStart
  );
}

export function isUrl(text: string, options: LinkifyOptions = {}): boolean {
  const autoDetectBareUrls = Boolean(options.autoDetectBareUrls);
  if (!buildUrlRegex(autoDetectBareUrls).test(text)) return false;
  if (autoDetectBareUrls && isBareUrlWithInvalidDomain(text)) return false;
  return true;
}

export function isUrlAndNotEmail(
  text: string,
  options: LinkifyOptions = {}
): boolean {
  if (emailFullMatchRegex.test(text)) return false;
  return isUrl(text, options);
}

export function linkifyTextNode(
  node: Node,
  options: LinkifyOptions = {}
): Node[] {
  const autoDetectBareUrls = Boolean(options.autoDetectBareUrls);

  const linkified = [];

  const urlRegex = buildUrlRegex(autoDetectBareUrls);
  const schema = node.type.schema;

  const emailRanges =
    autoDetectBareUrls && node.text ? getEmailRanges(node.text) : [];

  let pos = 0;
  let matchedUrlText = node.text && urlRegex.exec(node.text);
  while (matchedUrlText) {
    const start = matchedUrlText.index;
    const end = start + matchedUrlText[0].length;

    if (autoDetectBareUrls && isInsideEmail(start, end, emailRanges)) {
      matchedUrlText = node.text && urlRegex.exec(node.text);
      continue;
    }

    if (autoDetectBareUrls && isBareUrlWithInvalidDomain(matchedUrlText[0])) {
      matchedUrlText = node.text && urlRegex.exec(node.text);
      continue;
    }

    const link = schema.marks.link;
    const initialText = start > 0 && node.text?.slice(pos, start);
    if (initialText) {
      linkified.push(schema.text(initialText, node.marks));
    }
    const urlText = node.text?.slice(start, end);
    if (urlText) {
      const punctuationMatch = urlText.match(
        /(?<url>.*)(?<endingPunctuation>[?.,]+)$/
      );
      const url = punctuationMatch?.groups?.url || urlText;
      const endingPunctuation = punctuationMatch?.groups?.endingPunctuation;
      const href = autoDetectBareUrls ? ensureUrlHasProtocol(url) : url;
      linkified.push(
        schema.text(url, link.create({ href }).addToSet(node.marks))
      );
      if (endingPunctuation) {
        linkified.push(schema.text(endingPunctuation, node.marks));
      }
    }
    pos = end;
    matchedUrlText = node.text && urlRegex.exec(node.text);
  }
  const finalText = node.text?.slice(pos);
  if (finalText) {
    linkified.push(schema.text(finalText, node.marks));
  }
  return linkified;
}

export function linkify(
  fragment: Fragment,
  options: LinkifyOptions = {}
): Fragment {
  const linkified: Node[] = [];
  for (let i = 0; i < fragment.childCount; i++) {
    const child = fragment.child(i);
    if (child.isText) {
      const isAlreadyLinked = child.marks.some(
        (mark) => mark.type.name === 'link'
      );
      if (isAlreadyLinked) {
        linkified.push(child);
      } else {
        linkified.push(...linkifyTextNode(child, options));
      }
    } else {
      linkified.push(child.copy(linkify(child.content, options)));
    }
  }
  return Fragment.fromArray(linkified);
}
